/**
 * The installation's own device fingerprint (`otto_device_fp`) — one key, one
 * writer (card `t_88c3d858`).
 *
 * The value is the `deviceId` this install presents as: the socket's own auth
 * field (`stores/socket.ts`) and the id the tracker files its presence reports
 * under (`composables/useLocationTracking.ts` → `POST /presence/location`).
 *
 * It used to be created only inside the socket's local connect, and the
 * tracker only READ it — answering `'unknown'` when absent, which made the
 * tracker bail silently. A session opened through the cloud relay never runs
 * the local socket path (`stores/socket.ts` swaps to the WebRTC status bridge
 * and returns early), so on a tunnel-only install the key was never written
 * and starting the tracker could never post anything. The read now creates
 * what is missing, exactly as the socket's own reader always did — and both
 * readers call this one function, so the two spellings cannot drift.
 */

const STORAGE_KEY = 'otto_device_fp';

/**
 * A fresh UUID v4. `crypto.randomUUID()` requires a secure context and Chrome
 * 92+, so older Android WebViews get the manual spelling.
 */
function freshFingerprint(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * The id for a storage that refuses writes: one per page session, so every
 * caller in the same session still agrees on who this device is.
 */
let memoryFallback: string | null = null;

/** The stored fingerprint, created and persisted when the key is absent. */
export function getOrCreateDeviceFingerprint(): string {
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
  } catch {
    // A storage that refuses reads: the in-memory id below still answers.
  }

  if (!memoryFallback) memoryFallback = freshFingerprint();
  try {
    localStorage.setItem(STORAGE_KEY, memoryFallback);
  } catch {
    /* ignore */
  }
  return memoryFallback;
}
